const CANONICAL_API_URL = "https://maisqserve.com.br/api";
const REQUEST_TIMEOUT_MS = 15000;

const isLocalHostname = (hostname = "") => {
  const normalized = String(hostname).trim().toLowerCase();
  return normalized === "localhost" ||
    normalized === "127.0.0.1" ||
    normalized === "::1" ||
    normalized.startsWith("10.") ||
    normalized.startsWith("192.168.") ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(normalized);
};

const normalizeApiUrl = (value) => {
  const parsed = new URL(String(value).trim());
  parsed.hash = "";
  parsed.search = "";
  parsed.pathname = parsed.pathname.replace(/\/+$/, "") || "/api";
  if (parsed.pathname === "/") parsed.pathname = "/api";
  return parsed.toString().replace(/\/$/, "");
};

const resolveApiBaseUrl = (
  configuredValue = process.env.REACT_APP_API_URL,
  browserLocation = typeof window !== "undefined" ? window.location : { hostname: "" }
) => {
  const configured = String(configuredValue || "").trim();
  if (!configured || configured.toLowerCase() === "auto") return CANONICAL_API_URL;

  try {
    const normalized = normalizeApiUrl(configured);
    const target = new URL(normalized);
    const browserIsLocal = isLocalHostname(browserLocation?.hostname);
    const targetIsLocal = isLocalHostname(target.hostname);
    const targetIsCanonical = target.protocol === "https:" &&
      ["maisqserve.com.br", "www.maisqserve.com.br"].includes(target.hostname.toLowerCase()) &&
      target.pathname === "/api";

    if (targetIsCanonical || (browserIsLocal && targetIsLocal && ["http:", "https:"].includes(target.protocol))) {
      return normalized;
    }
  } catch (_) {
    // Invalid or unsafe overrides fall back to the public canonical endpoint.
  }

  return CANONICAL_API_URL;
};

const BASE_URL = resolveApiBaseUrl();

const getToken = () => localStorage.getItem("qserve_token");

const isPrivateUploadUrl = (value) => {
  try {
    const url = new URL(value);
    const api = new URL(BASE_URL);
    const uploads = `${api.pathname.replace(/\/$/, "")}/uploads/`;
    return url.origin === api.origin &&
      url.search === "" && url.hash === "" &&
      ["documents/", "liveness/"].some((directory) => url.pathname.startsWith(uploads + directory));
  } catch (_) {
    return false;
  }
};

const fetchPrivateUpload = async (url) => {
  if (!isPrivateUploadUrl(url)) throw new Error("URL de arquivo privado invalida.");
  const token = getToken();
  if (!token) throw new Error("Autenticacao necessaria para visualizar o arquivo.");
  const response = await fetchWithTimeout(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new Error(`Arquivo indisponivel (${response.status}).`);
  return response.blob();
};

const detectClientPlatform = () => {
  if (typeof navigator === "undefined" || typeof window === "undefined") return "web";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  const touchMobile = (navigator.maxTouchPoints || 0) > 0 && window.innerWidth <= 1024;
  if (/Mobile|Windows Phone|BlackBerry|Opera Mini|IEMobile|webOS/i.test(ua) || touchMobile) {
    return "mobile";
  }
  return "web";
};

const responseData = async (response) => {
  const contentType = String(response.headers?.get?.("content-type") || "").toLowerCase();
  if (!contentType.includes("application/json")) return {};
  return response.json().catch(() => ({}));
};

const publicErrorMessage = (data, status) => {
  const candidate = typeof data?.error === "string" ? data.error.trim() : "";
  return candidate && candidate.length <= 300 ? candidate : `Erro ${status}`;
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    if (error?.name === "AbortError") throw new Error("A requisição demorou além do esperado. Tente novamente.");
    throw new Error("Não foi possível conectar ao serviço. Verifique sua conexão e tente novamente.");
  } finally {
    clearTimeout(timer);
  }
};

const request = async (method, path, body, token, { idempotencyKey } = {}) => {
  const headers = {
    "Content-Type": "application/json",
    "X-Qserve-Client-Platform": detectClientPlatform(),
  };
  const t = token || getToken();
  if (t) headers["Authorization"] = `Bearer ${t}`;
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;

  const res = await fetchWithTimeout(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await responseData(res);
  if (!res.ok) {
    const error = new Error(publicErrorMessage(data, res.status));
    error.status = res.status;
    throw error;
  }
  return data;
};

const uploadFile = async (path, formData) => {
  const headers = { "X-Qserve-Client-Platform": detectClientPlatform() };
  const t = getToken();
  if (t) headers["Authorization"] = `Bearer ${t}`;

  const res = await fetchWithTimeout(`${BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: formData,
  }, 60000);

  const data = await responseData(res);
  if (!res.ok) {
    const error = new Error(publicErrorMessage(data, res.status));
    error.status = res.status;
    throw error;
  }
  return data;
};

const uploadPublicFile = async (path, formData) => {
  const headers = { "X-Qserve-Client-Platform": detectClientPlatform() };
  const res = await fetchWithTimeout(`${BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: formData,
  }, 60000);

  const data = await responseData(res);
  if (!res.ok) {
    const error = new Error(publicErrorMessage(data, res.status));
    error.status = res.status;
    throw error;
  }
  return data;
};

const api = {
  auth: {
    login: (email, password) => request("POST", "/auth/login", { email, password }),
    register: (payload) => request("POST", "/auth/register", payload),
    registerWorker: (formData) => uploadPublicFile("/auth/register-worker", formData),
    googleStatus: () => request("GET", "/auth/google/status"),
    googleSession: (session) => request("POST", "/auth/google/session", { session }),
    googleStartUrl: ({ mode = "login", role = "client", next = "", partnerSlug = "", workerReferralId = "", conductAccepted = false, conductTermVersion = "" } = {}) => {
      const params = new URLSearchParams({ mode, role });
      if (next) params.set("next", next);
      if (partnerSlug) params.set("partner", partnerSlug);
      if (workerReferralId) params.set("worker", workerReferralId);
      if (mode === "register") {
        params.set("conductAccepted", conductAccepted ? "1" : "0");
        if (conductTermVersion) params.set("conductTermVersion", conductTermVersion);
      }
      params.set("client_redirect", `${window.location.origin}/login`);
      return `${BASE_URL}/auth/google/start?${params.toString()}`;
    },
    me: () => request("GET", "/auth/me"),
    updateProfile: (payload) => request("PATCH", "/auth/profile", payload),
    forgotPassword: (email) => request("POST", "/auth/forgot-password", { email }),
    resetPassword: (token, newPassword) => request("POST", "/auth/reset-password", { token, newPassword }),
    changePassword: (oldPassword, newPassword) => request("PATCH", "/auth/change-password", { oldPassword, newPassword }),
  },
  terms: {
    conduct: () => request("GET", "/terms/conduct"),
  },
  contact: {
    send: (payload) => request("POST", "/contact", payload),
  },
  jobs: {
    list: (status) => request("GET", `/jobs${status ? `?status=${status}` : ""}`),
    get: (id) => request("GET", `/jobs/${id}`),
    create: (payload, idempotencyKey) => request("POST", "/jobs", payload, undefined, { idempotencyKey }),
    accept: (id) => request("PATCH", `/jobs/${id}/accept`),
    validateToken: (id, token) => request("PATCH", `/jobs/${id}/validate-token`, { token }),
    rate: (id, score, comment) => request("POST", `/jobs/${id}/rate`, { score, comment }),
    cancel: (id) => request("PATCH", `/jobs/${id}/cancel`),
    start: (id) => request("PATCH", `/jobs/${id}/start`),
    proposePrice: (id, price) => request("PATCH", `/jobs/${id}/propose-price`, { price }),
  },
  priceTable: {
    list: () => request("GET", "/price-table"),
    adminList: () => request("GET", "/admin/price-table"),
    create: (payload) => request("POST", "/admin/price-table", payload),
    update: (id, payload) => request("PUT", `/admin/price-table/${id}`, payload),
    remove: (id) => request("DELETE", `/admin/price-table/${id}`),
  },
  documents: {
    upload: (formData) => uploadFile("/workers/documents", formData),
    myList: () => request("GET", "/workers/documents"),
    adminList: (workerId) => request("GET", `/admin/documents/${workerId}`),
    updateStatus: (docId, status) => request("PATCH", `/admin/documents/${docId}/status`, { status }),
  },
  liveness: {
    start: () => request("POST", "/workers/liveness/start"),
    submit: (id, formData) => uploadFile(`/workers/liveness/${id}/submit`, formData),
    status: () => request("GET", "/workers/liveness"),
    adminStatus: (workerId) => request("GET", `/admin/workers/${workerId}/liveness`),
    review: (workerId, id, decision) => request("PATCH", `/admin/workers/${workerId}/liveness/${id}/review`, { decision }),
  },
  withdrawals: {
    request: (payload) => request("POST", "/workers/withdrawals", payload),
    myList: () => request("GET", "/workers/withdrawals"),
    adminList: () => request("GET", "/admin/withdrawals"),
    approve: (id) => request("PATCH", `/admin/withdrawals/${id}/approve`),
    reject: (id, reason) => request("PATCH", `/admin/withdrawals/${id}/reject`, { reason }),
  },
  promotions: {
    list: () => request("GET", "/promotions"),
    adminList: () => request("GET", "/admin/promotions"),
    create: (payload) => request("POST", "/admin/promotions", payload),
    update: (id, payload) => request("PUT", `/admin/promotions/${id}`, payload),
    remove: (id) => request("DELETE", `/admin/promotions/${id}`),
  },
  partners: {
    referral: (slug) => request("GET", `/partners/referral/${encodeURIComponent(slug)}`),
    resolveLocation: (params = {}) => request("GET", `/partners/resolve-location?${new URLSearchParams(params)}`),
  },
  admin: {
    dashboard: () => request("GET", "/admin/dashboard"),
    geoImpact: () => request("GET", "/admin/geo-impact"),
    users: (role) => request("GET", `/admin/users${role ? `?role=${role}` : ""}`),
    allJobs: (status) => request("GET", `/admin/jobs${status ? `?status=${status}` : ""}`),
    approveWorker: (id) => request("PATCH", `/admin/workers/${id}/approve`),
    rejectWorker: (id) => request("PATCH", `/admin/workers/${id}/reject`),
    setWorkerStatus: (id, status) => request("PATCH", `/admin/workers/${id}/status`, { status }),
    deactivateUser: (id) => request("PATCH", `/admin/users/${id}/deactivate`),
    deleteUser: (id) => request("DELETE", `/admin/users/${id}`),
    updateBackgroundCheck: (id, status, notes) => request("PATCH", `/admin/workers/${id}/background-check`, { status, notes }),
    getUserById: (id) => request("GET", `/admin/users/${id}`),
    resetUserPassword: (id) => request("POST", `/admin/users/${id}/reset-password`),
    activateUser: (id) => request("PATCH", `/admin/users/${id}/activate`),
    getSettings: () => request("GET", "/admin/settings"),
    updateSettings: (payload) => request("PATCH", "/admin/settings", payload),
    conductAcceptances: () => request("GET", "/admin/conduct-acceptances"),
    deleteJob: (id) => request("DELETE", `/admin/jobs/${id}`),
    reviews: () => request("GET", "/admin/reviews"),
    deleteReview: (id) => request("DELETE", `/admin/reviews/${id}`),
    partners: () => request("GET", "/admin/partners"),
    createPartner: (payload) => request("POST", "/admin/partners", payload),
    updatePartner: (id, payload) => request("PUT", `/admin/partners/${id}`, payload),
    deletePartner: (id) => request("DELETE", `/admin/partners/${id}`),
    createPartnerUser: (id, payload) => request("POST", `/admin/partners/${id}/users`, payload),
    assignPartnerJobs: (id) => request("POST", `/admin/partners/${id}/assign-jobs`),
  },
  partner: {
    dashboard: () => request("GET", "/partner/dashboard"),
    jobs: (status) => request("GET", `/partner/jobs${status ? `?status=${status}` : ""}`),
    clients: () => request("GET", "/partner/clients"),
    workers: () => request("GET", "/partner/workers"),
    withdrawals: () => request("GET", "/partner/withdrawals"),
    requestWithdrawal: (payload) => request("POST", "/partner/withdrawals", payload),
  },
  reviews: {
    create: (payload) => request("POST", "/reviews", payload),
    byWorker: (workerId) => request("GET", `/reviews/worker/${workerId}`),
    byJob: (jobId) => request("GET", `/reviews/job/${jobId}`),
  },
  payments: {
    createCharge: (jobId) => request("POST", "/payments/create", { jobId }),
    mockConfirm: (jobId) => request("POST", "/payments/mock-confirm", { jobId }),
    status: (jobId) => request("GET", `/payments/${jobId}/status`),
    pendingRelease: () => request("GET", "/payments/pending-release"),
    selectDisbursementPath: (jobId, path) => request("POST", "/payments/disbursement-path", { jobId, path }),
    approveRelease: (jobId) => request("POST", "/payments/release", { jobId }),
    rejectRelease: (jobId, reason) => request("POST", "/payments/reject", { jobId, reason }),
    confirmTransfer: (jobId) => request("POST", "/payments/confirm-transfer", { jobId }),
    transferToWorker: (jobId) => request("POST", "/payments/transfer", { jobId }),
    testConnection: () => request("POST", "/payments/test-asaas"),
  },
  accounting: {
    entries: (params = {}) => request("GET", `/accounting/entries?${new URLSearchParams(params)}`),
    createEntry: (data) => request("POST", "/accounting/entries", data),
    updateEntry: (id, data) => request("PUT", `/accounting/entries/${id}`, data),
    deleteEntry: (id) => request("DELETE", `/accounting/entries/${id}`),
    bulkCreate: (entries) => request("POST", "/accounting/entries/bulk", { entries }),
    categories: () => request("GET", "/accounting/categories"),
    createCategory: (data) => request("POST", "/accounting/categories", data),
    updateCategory: (id, data) => request("PUT", `/accounting/categories/${id}`, data),
    deleteCategory: (id) => request("DELETE", `/accounting/categories/${id}`),
    summary: (params = {}) => request("GET", `/accounting/reports/summary?${new URLSearchParams(params)}`),
    dre: (params = {}) => request("GET", `/accounting/reports/dre?${new URLSearchParams(params)}`),
    cashflow: (params = {}) => request("GET", `/accounting/reports/cashflow?${new URLSearchParams(params)}`),
    balance: () => request("GET", "/accounting/reports/balance"),
    bank: (params = {}) => request("GET", `/accounting/bank?${new URLSearchParams(params)}`),
    createBank: (data) => request("POST", "/accounting/bank", data),
    reconcile: (id, entryId) => request("POST", `/accounting/bank/reconcile/${id}`, { entryId }),
    exportUrl: (type, params = {}) => `${BASE_URL}/accounting/reports/export/${type}?${new URLSearchParams(params)}`,
    downloadExport: async (type, params = {}) => {
      const token = getToken();
      const url = `${BASE_URL}/accounting/reports/export/${type}?${new URLSearchParams(params)}`;
      const res = await fetchWithTimeout(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} }, 30000);
      if (!res.ok) throw new Error("Falha ao exportar");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${type}-${params.year || ""}-${params.month || ""}.${type === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    },
  },
};

export default api;
export { api };
export { BASE_URL as API_BASE_URL };
export { CANONICAL_API_URL, isLocalHostname, normalizeApiUrl, resolveApiBaseUrl };
export { isPrivateUploadUrl, fetchPrivateUpload };
